// Typed client for the property-review REST surface (PROP-72 / PROP-74).
//
// Five endpoints under /api/properties/{name}/reviews:
//
//   GET    .../reviews?limit&cursor         anonymous — feed + aggregate
//   POST   .../reviews                      auth — create
//   PATCH  .../reviews/{id}                 auth, author-only
//   DELETE .../reviews/{id}                 auth, author-only, soft delete
//   POST   .../reviews/{id}/report          auth — flag for moderation
//
// Wire shapes match backend/internal/reviews/store.go field-for-field;
// keep them in sync. Optimistic UI rule (CLAUDE.md): the create / update
// handlers return the FULL hydrated row — the FE patches the in-memory
// row from that response, never reconstructs from form state, so the
// live-committed view stays symmetric with the post-refresh re-hydrated
// view.

import { apiJson, apiPostJson, apiPatchJson, apiDelete } from "./api";

export interface Review {
  id: string;
  project_name: string;
  author_user_id: string;
  author_name: string;
  rating: number;
  body: string;
  status: string;
  report_count: number;
  created_at: string;
  updated_at: string;
}

export interface ReviewAggregate {
  avg: number;
  count: number;
}

export interface ReviewListResponse {
  reviews: Review[] | null;
  aggregate: ReviewAggregate;
  next_cursor?: string;
}

// Closed report-reason set — mirrors constants.ReviewReportReasons on
// the backend. Keep in sync; the server validates against the same list
// and returns 400 on anything else.
export const REVIEW_REPORT_REASONS = [
  "spam",
  "offensive",
  "off_topic",
  "misinformation",
  "other",
] as const;
export type ReviewReportReason = (typeof REVIEW_REPORT_REASONS)[number];

// Body length cap (backend constants.ReviewBodyMaxLen). Mirrored here so
// the FE can render a live counter and block submission BEFORE the
// server 400s — the server still enforces it, but a clean client-side
// guard is the better UX.
export const REVIEW_BODY_MAX_LEN = 4000;
export const REVIEW_RATING_MIN = 1;
export const REVIEW_RATING_MAX = 5;

function reviewsPath(projectName: string): string {
  return `/api/properties/${encodeURIComponent(projectName)}/reviews`;
}

// listReviews returns one page of visible reviews + the project-wide
// aggregate. limit defaults server-side; pass undefined to accept that.
// cursor null/undefined fetches the first page.
export async function listReviews(
  projectName: string,
  opts?: { limit?: number; cursor?: string },
): Promise<ReviewListResponse> {
  const params: string[] = [];
  if (opts?.limit) params.push(`limit=${opts.limit}`);
  if (opts?.cursor) params.push(`cursor=${encodeURIComponent(opts.cursor)}`);
  const q = params.length ? `?${params.join("&")}` : "";
  const resp = await apiJson<ReviewListResponse>(reviewsPath(projectName) + q);
  // The server emits `null` for an empty array; normalise so callers
  // can always `.map` without a guard.
  return { ...resp, reviews: resp.reviews ?? [] };
}

export interface CreateReviewInput {
  rating: number;
  body: string;
}

// createReview posts a new review and returns the hydrated server row
// (with id, author_name, timestamps). Throws ApiError — 401 on no
// session, 404 on unknown project, 409 on duplicate, 400 on input.
export async function createReview(
  projectName: string,
  input: CreateReviewInput,
): Promise<Review> {
  return apiPostJson<Review>(reviewsPath(projectName), input);
}

// updateReview applies a rating/body patch. Author-scoped on the server.
// Throws ApiError — 401 unauth, 403 not the author, 404 wrong project /
// id / already-deleted.
export async function updateReview(
  projectName: string,
  id: string,
  input: CreateReviewInput,
): Promise<Review> {
  return apiPatchJson<Review>(`${reviewsPath(projectName)}/${encodeURIComponent(id)}`, input);
}

// deleteReview soft-deletes (status='deleted'). Author-scoped on the
// server; idempotent — a second call on an already-deleted row still
// returns ok.
export async function deleteReview(projectName: string, id: string): Promise<void> {
  await apiDelete(`${reviewsPath(projectName)}/${encodeURIComponent(id)}`);
}

export interface ReportReviewInput {
  reason: ReviewReportReason;
  note?: string;
}

export interface ReportReviewResponse {
  ok: boolean;
  already_reported: boolean;
}

// reportReview flags a review for moderation. The same (reviewer,
// review) pair is a no-op on the second call — already_reported=true
// signals the duplicate so the UI can soften the toast.
export async function reportReview(
  projectName: string,
  id: string,
  input: ReportReviewInput,
): Promise<ReportReviewResponse> {
  return apiPostJson<ReportReviewResponse>(
    `${reviewsPath(projectName)}/${encodeURIComponent(id)}/report`,
    input,
  );
}

// ── pure helpers (testable; no network) ────────────────────────────────

// reviewBodyByteLength returns the UTF-8 byte length of s — this is the
// metric the backend caps against (`len(body)` in Go counts bytes, not
// runes). The FE must agree byte-for-byte; using JS .length (UTF-16
// code units) here would let a 1500-character Chinese review (4500
// UTF-8 bytes) pass client-side validation and 400 from the server.
export function reviewBodyByteLength(s: string): number {
  // TextEncoder is available in every modern browser + Node 11+. The
  // Vitest jsdom env exposes it too.
  return new TextEncoder().encode(s).byteLength;
}

// validateReviewInput mirrors the backend's validateReviewInput shape
// (range-check rating; trim + non-empty + maxlen body — measured in
// UTF-8 bytes to match the server) so the FE blocks known-bad
// submissions BEFORE they reach the network. The server still
// validates — this is purely a UX guard so the user gets feedback at
// the keystroke level instead of after a round trip.
//
// Returns an i18n key on failure (not a string) so the component owns
// translation; this keeps the helper pure for unit testing.
export type ValidatedReviewInput =
  | { ok: true; rating: number; body: string }
  | { ok: false; errKey: "reviews.errRatingRange" | "reviews.errBodyEmpty" | "reviews.errBodyTooLong" };

export function validateReviewInput(rating: number, body: string): ValidatedReviewInput {
  if (!Number.isInteger(rating) || rating < REVIEW_RATING_MIN || rating > REVIEW_RATING_MAX) {
    return { ok: false, errKey: "reviews.errRatingRange" };
  }
  const trimmed = body.trim();
  if (trimmed.length === 0) {
    return { ok: false, errKey: "reviews.errBodyEmpty" };
  }
  if (reviewBodyByteLength(trimmed) > REVIEW_BODY_MAX_LEN) {
    return { ok: false, errKey: "reviews.errBodyTooLong" };
  }
  return { ok: true, rating, body: trimmed };
}

// Aggregate recomputation helpers — the FE patches {avg, count} live so
// the modal header doesn't lag a re-fetch behind every mutation. These
// match the SQL the backend's ListVisible would produce on the same set,
// so the optimistic value equals the post-refresh value (CLAUDE.md
// symmetry rule).

export function aggregateAfterCreate(agg: ReviewAggregate, newRating: number): ReviewAggregate {
  const count = agg.count + 1;
  return { count, avg: (agg.avg * agg.count + newRating) / count };
}

export function aggregateAfterDelete(agg: ReviewAggregate, removedRating: number): ReviewAggregate {
  if (agg.count <= 1) return { avg: 0, count: 0 };
  const count = agg.count - 1;
  return { count, avg: (agg.avg * agg.count - removedRating) / count };
}

export function aggregateAfterEdit(
  agg: ReviewAggregate,
  oldRating: number,
  newRating: number,
): ReviewAggregate {
  if (agg.count === 0) return agg; // defensive — shouldn't happen, but don't /0
  return { count: agg.count, avg: (agg.avg * agg.count + (newRating - oldRating)) / agg.count };
}
